import React, { useCallback } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import PaginationItem from '@mui/material/PaginationItem'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  UI_COLOR_BORDER_DISABLED,
  UI_COLOR_BORDER_SOFT,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_SURFACE_DISABLED,
  UI_COLOR_TEXT_BODY,
  UI_COLOR_TEXT_DISABLED,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import {
  mergeMobilePaginationNavSx,
  mergeMobilePaginationPaperSx,
  MOBILE_PAGINATION_CAPTION_SX
} from './styles/mobilePaginationBarSx'

export default function MobilePaginationBar({
  current_page: currentPageProp,
  total_pages: totalPagesProp,
  onNavigate,
  onPageChange,
  handle_pagination: handlePaginationLegacy,
  ariaLabel = 'Paginación',
  navClassName,
  navSx,
  paperClassName,
  paperSx,
  captionClassName,
  disablePaper = false,
  disabled = false
}) {
  const current = Number(currentPageProp) || 1
  const total = Number(totalPagesProp) || 1
  const supportsDirectPageChange = typeof onPageChange === 'function'

  const handleNavigate = useCallback(
    (id) => () => {
      if (typeof onNavigate === 'function') {
        onNavigate(id)
        return
      }
      if (typeof handlePaginationLegacy === 'function') {
        handlePaginationLegacy({
          preventDefault() {},
          target: { tagName: 'A', id }
        })
      }
    },
    [onNavigate, handlePaginationLegacy]
  )

  const handleDirectPageChange = useCallback(
    (_, page) => {
      if (!supportsDirectPageChange || page === current) return
      onPageChange(page)
    },
    [current, onPageChange, supportsDirectPageChange]
  )

  const paginationContent = (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        className={captionClassName}
        sx={MOBILE_PAGINATION_CAPTION_SX}
      >
        Página <Box component="span" sx={{ color: UI_COLOR_PRIMARY, fontWeight: 800 }}>{current}</Box> de{' '}
        <Box component="span" sx={{ color: UI_COLOR_PRIMARY, fontWeight: 800 }}>{total}</Box>
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Pagination
          disabled={disabled}
          page={current}
          count={Math.max(total, 1)}
          onChange={supportsDirectPageChange ? handleDirectPageChange : undefined}
          shape="rounded"
          variant="outlined"
          size="medium"
          siblingCount={0}
          boundaryCount={1}
          showFirstButton
          showLastButton
          renderItem={(item) => {
            const navigationClickMap = {
              first: handleNavigate('first-page'),
              previous: handleNavigate('previous-page'),
              next: handleNavigate('next-page'),
              last: handleNavigate('last-page'),
              page: handleNavigate(String(item.page))
            }

            return (
              <PaginationItem
                {...item}
                onClick={!supportsDirectPageChange ? navigationClickMap[item.type] : item.onClick}
                slots={{
                  first: FirstPageIcon,
                  previous: ChevronLeftIcon,
                  next: ChevronRightIcon,
                  last: LastPageIcon
                }}
                sx={{
                  mx: 0.25,
                  minWidth: item.type === 'page' ? 38 : 40,
                  height: 38,
                  borderRadius: 1.75,
                  fontSize: 15,
                  fontWeight: 700,
                  borderColor: UI_COLOR_BORDER_SOFT,
                  color: UI_COLOR_TEXT_BODY,
                  bgcolor: UI_COLOR_WHITE,
                  '&.Mui-selected': {
                    bgcolor: UI_COLOR_PRIMARY,
                    color: UI_COLOR_WHITE,
                    borderColor: UI_COLOR_PRIMARY,
                    '&:hover': {
                      bgcolor: UI_COLOR_PRIMARY_HOVER,
                      borderColor: UI_COLOR_PRIMARY_HOVER
                    }
                  },
                  '&.Mui-disabled': {
                    borderColor: UI_COLOR_BORDER_DISABLED,
                    color: UI_COLOR_TEXT_DISABLED,
                    bgcolor: UI_COLOR_SURFACE_DISABLED
                  }
                }}
              />
            )
          }}
          sx={{
            '& .MuiPagination-ul': {
              flexWrap: 'nowrap',
              gap: 0.2
            }
          }}
        />
      </Box>
    </>
  )

  return (
    <Box
      component="nav"
      aria-label={ariaLabel}
      className={navClassName}
      sx={mergeMobilePaginationNavSx(navSx)}
    >
      {disablePaper ? (
        <Box sx={{ width: '100%' }}>{paginationContent}</Box>
      ) : (
        <Paper
          elevation={0}
          className={paperClassName}
          sx={mergeMobilePaginationPaperSx(paperSx)}
        >
          {paginationContent}
        </Paper>
      )}
    </Box>
  )
}
